<template>
  <div class="row donation-campaigns">
    <div class="col-xl-12 col-md-12 col-xs-12">
      <div class="card mar-to-15 tableInvoice">
        <div class="card-content">

          <!-- LOADING OVERLAY -->
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>

          <!-- TOP BAR -->
          <div class="table-top" ref="tableTop" style="display: flex; justify-content: space-between;align-items: flex-start;gap: 10px;flex-wrap: wrap;flex-direction: column;">

            <!-- LEFT: FILTERS + SEARCH -->
            <div class="left-group" style="display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end; flex: 1 1 auto;">

              <!-- SEARCH -->
              <div class="form-group" style="flex: 1 1 200px; margin: 0;">
                <label>{{ $t('donationCampaigns.search') }}</label>
                <el-input
                  v-model="search"
                  :placeholder="$t('donationCampaigns.search')"
                  size="small"
                  clearable
                />
              </div>

              <!-- STATUS -->
              <div class="form-group" style="flex: 0 0 140px; margin: 0;">
                <label>{{ $t('orders.status') }}</label>
                <v-select
                  v-model="statusFilter"
                  :options="[
                    { id: 1, type: $t('general.active') },
                    { id: 0, type: $t('general.inactive') }
                  ]"
                  label="type"
                  :reduce="option => option.id"
                  :placeholder="$t('general.select')"
                  clearable
                ></v-select>
              </div>

              <!-- LOCATIONS -->
              <div class="form-group donation-campaigns__location-filter" v-if="locations.length" style="margin: 0;">
                <label>{{ $t('orders.location') }}</label>
                <el-select
                  v-model="locationFilter"
                  multiple
                  collapse-tags
                  clearable
                  filterable
                  size="small"
                  :placeholder="$t('general.select')"
                  style="width: 100%;">
                  <el-option
                    v-for="loc in locations"
                    :key="getLocationOptionValue(loc)"
                    :label="loc.name"
                    :value="getLocationOptionValue(loc)">
                  </el-option>
                </el-select>
              </div>

              <!-- DATE RANGE -->
              <div class="form-group" style="flex: 0 0 220px; margin: 0;">
                <label>{{ $t('donationCampaigns.startEnd') }}</label>
                <el-date-picker
                  type="daterange"
                  v-model="dateRangeFilter"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="small"
                  :placeholder="$t('donationCampaigns.startEnd')"
                ></el-date-picker>
              </div>

              <!-- SEARCH BUTTON -->
              <div class="form-group" style="flex: 0 0 100px; margin: 0;">
                <button class="btn add" @click="applyFilters" :title="$t('reports.search')" style="width: 100%; font-size: 12px; padding: 4px 8px;">
                  <i class="ti-search"></i> {{ $t('general.search') }}
                </button>
              </div>

            </div>

            <!-- RIGHT: ADD BUTTON -->
            <div class="right-group" style="flex: 0 0 auto; margin-bottom: 20px; margin-top: 10px;">
              <button class="btn add" @click.prevent="goToAdd" :title="$t('general.add') + ' ' + $t('donationCampaigns.single')" style="font-size: 12px; padding: 4px 8px;">
                <img src="/static/img/icons/icon-plus-sm.png" /> {{ $t('general.add') }} {{ $t('donationCampaigns.single') }}
              </button>
            </div>

          </div>

          <p class="donation-campaigns__info">
            <i class="fa fa-info-circle" aria-hidden="true"></i> {{ $t('donationCampaigns.onlyOneActivePerLocation') }}
          </p>
          <p class="donation-campaigns__info">
            <i class="fa fa-info-circle" aria-hidden="true"></i>
            {{ $t('donationCampaigns.fiscalizationInfoPrefix') }}<router-link :to="{ path: '/settings', query: { tabIndex: '1' } }" class="donation-campaigns__info-link">{{ $t('donationCampaigns.fiscalizationInfoLink') }}</router-link>{{ $t('donationCampaigns.fiscalizationInfoSuffix', { field: $t('settingCompany.donation_vat_printer_id') }) }}
          </p>

          <!-- TABLE -->
          <div class="data-table" style="margin-top: 20px;">
            <el-table
              :data="tableData"
              :row-class-name="rowClass"
              :empty-text="$t('gestiune.noData')"
              style="width: 100%;"
              @row-click="onRowClick">
              <el-table-column prop="id" :label="$t('donationCampaigns.id')" width="80"/>
              <el-table-column prop="name" :label="$t('donationCampaigns.name')"/>
              <el-table-column :label="$t('donationCampaigns.location')" min-width="140">
                <template slot-scope="props">
                  <div class="locations-cell" :title="getLocationNames(props.row)">
                    <span class="locations-name">{{ getVisibleLocationName(props.row) }}</span>
                    <span v-if="getHiddenLocationCount(props.row)" class="locations-more">
                      +{{ getHiddenLocationCount(props.row) }}
                    </span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.startEnd')" min-width="180" class-name="date-range-cell">
                <template slot-scope="props">
                  {{ formatDate(props.row.starts_at || props.row.date_start) }} - {{ formatDate(props.row.ends_at || props.row.date_end) }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.daysLeft')" width="100">
                <template slot-scope="props">
                  {{ getDaysLeft(props.row.ends_at || props.row.date_end) }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.targetAmount')" width="140">
                <template slot-scope="props">
                  {{ parseFloat(props.row.target_amount || 0).toFixed(2) }} {{ currency }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('donationCampaigns.collectedAmount')" width="140">
                <template slot-scope="props">
                  {{ parseFloat(props.row.raised_total || 0).toFixed(2) }} {{ currency }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('general.active')" width="100">
                <template slot-scope="scope">
                  <div @click.stop>
                    <p-switch
                      v-model="scope.row.active"
                      type="success"
                      @input="handleActive(scope.$index, scope.row, $event)"
                    />
                  </div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('general.actions')" width="120">
                <template slot-scope="scope">
                  <el-dropdown trigger="click" placement="bottom-end" @click.native.stop>
                    <span class="arrow-box">&#9662;</span>
                    <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu">
                      <el-dropdown-item @click.native.stop="goToRecord(scope.row, null, $event)">
                        {{ $t('general.edit') }}
                      </el-dropdown-item>
                      <el-dropdown-item @click.native.stop="handleDelete(scope.$index, scope.row)">
                        {{ $t('general.delete') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </template>
              </el-table-column>
            </el-table>

            <!-- PAGINATION -->
            <el-pagination
              layout="prev, pager, next"
              :total="paginationDef.totalRecords"
              :page-size="paginationDef.perPage"
              :current-page.sync="paginationDef.page"
              @current-change="handlePageChange"
              style="margin-top: 15px; text-align: right;"
            />
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Input, Select, Option, DatePicker } from 'element-ui'
import DataTables from 'vue-data-tables'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import vSelect from 'vue-select'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'

Vue.component('v-select', vSelect)
Vue.use(Input)
Vue.use(Select)
Vue.use(Option)
Vue.use(DatePicker)
Vue.use(DataTables)

export default {
  components: {
    PSwitch
  },
  props: {
    isDonationCampaigns: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      onInputGetCampaignsTimeout: null,
      search: '',
      statusFilter: null,
      locationFilter: [],
      dateRangeFilter: [],
      locations: [],
      tableData: [],
      currency: this.getCurrencySymbol(this.$session.get('selectedCompanyData')['currency_iso']),
      paginationDef: {
        page: 1,
        perPage: 20,
        totalPages: null,
        totalRecords: null,
        show: true
      }
    }
  },
  created () {
    this.initializePage()
    this.$bus.$on('refresh', this.handleRefresh)
  },
  beforeDestroy () {
    this.$bus.$off('refresh', this.handleRefresh)
  },
  methods: {
    handleRefresh () {
      this.getCampaigns()
    },
    initializePage () {
      this.getLocations().then(() => {
        this.getCampaigns()
      })
    },
    getLocations () {
      return new Promise((resolve) => {
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/get-locations', 'GET', data, (response) => {
          if (response.status) {
            this.locations = response.data || []
          } else {
            this.locations = (this.$session.get('selectedCompanyData') || {}).locations_details || []
          }
          if (!this.isOwner()) {
            let allowedLocations = JSON.parse(this.$session.get('userDataLocations') || '[]')
            if (allowedLocations && allowedLocations.length > 0) {
              this.locations = this.locations.filter(loc =>
                allowedLocations.some(allowedId => this.locationMatchesId(loc, allowedId))
              )
            }
          }
          resolve()
        })
      })
    },
    locationIdMatches (a, b) {
      if (a === b) return true
      if (a == null || b == null || a === '' || b === '') return false
      return String(a) === String(b)
    },
    locationMatchesId (loc, locationId) {
      return this.locationIdMatches(loc.id, locationId) || this.locationIdMatches(loc.true_id, locationId)
    },
    getLocationOptionValue (loc) {
      return loc.true_id != null ? loc.true_id : loc.id
    },
    normalizeLocationFilterValue (value) {
      if (value && typeof value === 'object') {
        return this.getLocationOptionValue(value)
      }
      return value
    },
    onRowClick (row, column, event) {
      if (column.property === 'active' || column.property === 'actions' || event.target.closest('.el-switch') || event.target.closest('.el-dropdown')) {
        return
      }
      this.goToRecord(row, null, event)
    },
    goToAdd () {
      this.$router.push({ path: '/marketing/donation-campaigns/add' })
    },
    goToRecord (row, column, event) {
      this.$router.push({ path: '/marketing/donation-campaigns/edit/' + row.id })
    },
    applyFilters () {
      this.paginationDef.page = 1
      this.getCampaigns()
    },
    onInput () {
      clearTimeout(this.onInputGetCampaignsTimeout)
      this.onInputGetCampaignsTimeout = setTimeout(() => {
        this.getCampaigns()
      }, 500)
    },
    handlePageChange (page) {
      this.paginationDef.page = page
      this.getCampaigns()
    },
    normalizeActiveFilterValue (value) {
      const rawValue = (value && typeof value === 'object' && 'id' in value) ? value.id : value
      if (rawValue === true || rawValue === 1 || rawValue === '1') return true
      if (rawValue === false || rawValue === 0 || rawValue === '0') return false
      return null
    },
    hasActiveFilter () {
      return this.normalizeActiveFilterValue(this.statusFilter) !== null
    },
    buildParams () {
      let params = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        page: this.paginationDef.page,
        perPage: this.paginationDef.perPage
      }
      if (this.search) {
        params.name = this.search
      }
      if (this.hasActiveFilter()) {
        params.active = this.normalizeActiveFilterValue(this.statusFilter)
      }
      if (this.locationFilter && this.locationFilter.length) {
        params.location_ids = this.locationFilter.map(value => this.normalizeLocationFilterValue(value))
      }
      if (this.dateRangeFilter && this.dateRangeFilter.length === 2) {
        params.date_start = this.dateRangeFilter[0]
        params.date_end = this.dateRangeFilter[1]
      }
      return params
    },
    getCampaigns () {
      this.loading = true
      const data = this.buildParams()
      this.apiRequest('/reports/donation-campaigns', 'GET', data, (response) => {
        if (response.status === true) {
          if (response.data && response.data.pagination) {
            this.tableData = response.data.data
            this.paginationDef.page = response.data.pagination.page
            this.paginationDef.perPage = response.data.pagination.perPage
            this.paginationDef.totalPages = response.data.pagination.totalPages
            this.paginationDef.totalRecords = response.data.pagination.totalRecords
          } else {
            this.tableData = response.data || []
            this.paginationDef.totalRecords = this.tableData.length
          }
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    isActiveCampaignExistsResponse (response) {
      return !!(response && response.errors && response.errors.code === 'ACTIVE_CAMPAIGN_EXISTS' && response.data)
    },
    getRowLocationNames (row) {
      if (row.locations_formatted && row.locations_formatted.length) {
        return row.locations_formatted.map(loc => loc.name)
      }
      const locationIds = row.location_ids
      if (!locationIds || !locationIds.length || !this.locations.length) {
        return []
      }
      return this.locations
        .filter(loc => locationIds.some(locationId => this.locationMatchesId(loc, locationId)))
        .map(loc => loc.name)
    },
    getVisibleLocationName (row) {
      const names = this.getRowLocationNames(row)
      if (names.length) {
        return names[0]
      }
      if (!row.location_ids || !row.location_ids.length) {
        return this.$t('donationCampaigns.allLocations')
      }
      return '-'
    },
    getHiddenLocationCount (row) {
      const names = this.getRowLocationNames(row)
      if (names.length <= 1) {
        return 0
      }
      return names.length - 1
    },
    getLocationNames (row) {
      const names = this.getRowLocationNames(row)
      if (names.length) {
        return names.join(', ')
      }
      if (!row.location_ids || !row.location_ids.length) {
        return this.$t('donationCampaigns.allLocations')
      }
      return '-'
    },
    rowClass ({ row }) {
      return row.active ? 'highlight-row' : ''
    },
    handleActiveCampaignExists (row, response) {
      const activeCampaign = (response.data.active_campaign || {})
      const message = this.$t('donationCampaigns.activeCampaignExistsActivateMessage', { name: activeCampaign.name || '' })
      const warning = this.$t('donationCampaigns.activeCampaignExistsWarning')

      swal({
        title: '',
        html: message + '<br><br><strong>' + warning + '</strong>',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no')
      }).then((result) => {
        if (result) {
          this.activateCampaign(row)
        }
      }).catch(() => {})
    },
    activateCampaign (row) {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        active: 1,
        force_active: true
      }
      this.apiRequest('/reports/donation-campaigns/' + row.id, 'PUT', data, (response) => {
        if (response.status) {
          swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          this.getCampaigns()
        } else if (this.isActiveCampaignExistsResponse(response)) {
          row.active = false
          this.handleActiveCampaignExists(row, response)
        } else {
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          row.active = false
        }
      })
    },
    handleActive (index, row, e) {
      let actionText = row.active ? this.$t('general.activate') : this.$t('general.deactivate')
      swal({
        text: this.$t('validations.areYouSureYouWantTo') + ` ${actionText} ${row.name}?`,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes')
      }).then((result) => {
        if (result) {
          const data = {
            token: this.$session.get('token'),
            company_id: this.$session.get('selectedCompany'),
            active: row.active ? 1 : 0
          }
          this.apiRequest('/reports/donation-campaigns/' + row.id, 'PUT', data, (response) => {
            if (response.status) {
              swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
              if (row.active) {
                this.getCampaigns()
              }
            } else if (this.isActiveCampaignExistsResponse(response)) {
              row.active = false
              this.handleActiveCampaignExists(row, response)
            } else {
              swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
              row.active = !row.active
            }
          })
        }
      }).catch(() => {
        row.active = !row.active
      })
    },
    handleDelete (index, row) {
      swal({
        text: this.$t('donationCampaigns.areYouSureDelete') + ' ' + row.name + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes')
      }).then((result) => {
        if (result) {
          const data = {
            token: this.$session.get('token'),
            company_id: this.$session.get('selectedCompany')
          }
          this.apiRequest('/reports/donation-campaigns/' + row.id, 'DELETE', data, (response) => {
            if (response.status === true) {
              swal('', this.$t('donationCampaigns.campaignDeleted'), 'success').catch(() => {})
              this.tableData.splice(index, 1)
            } else {
              swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
            }
          })
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
.donation-campaigns .form-group {
  margin-bottom: 15px;
}
.donation-campaigns__info {
  margin: 0 0 10px;
  color: #666;
  font-size: 13px;
}
.donation-campaigns__info-link {
  font-weight: 600;
  text-decoration: underline;
}
.donation-campaigns__location-filter {
  flex: 0 0 300px;
  min-width: 280px;
  max-width: 340px;
}
.donation-campaigns__location-filter >>> .el-select__tags {
  max-width: calc(100% - 24px);
  flex-wrap: nowrap;
  overflow: hidden;
}
.donation-campaigns__location-filter >>> .el-select__tags .el-tag:first-of-type {
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.donation-campaigns__location-filter >>> .el-select__tags .el-tag:last-of-type:not(:only-of-type) {
  max-width: none;
  min-width: 36px;
  flex-shrink: 0;
  overflow: visible;
  text-overflow: clip;
}
.tableInvoice .el-table .cell {
  word-break: break-word;
}
.tableInvoice .el-table .date-range-cell .cell {
  white-space: nowrap;
  word-break: normal;
}
.arrow-box {
  display: inline-block;
  font-size: 18px;
  padding: 4px 6px;
  border: 1px solid #674BD2 !important;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  color: #674BD2;
}
.arrow-box:hover {
  background-color: #674BD2 !important;
  border-color: #674BD2 !important;
  color: #fff !important;
}
.custom-dropdown-menu {
  min-width: 180px;
  font-size: 14px;
}
.el-pagination {
  text-align: center !important;
}
.locations-cell {
  align-items: center;
  display: flex;
  max-width: 100%;
  overflow: hidden;
}
.locations-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.locations-more {
  background: #674BD2;
  border-radius: 10px;
  color: #fff;
  flex: 0 0 auto;
  font-size: 11px;
  line-height: 1;
  margin-left: 6px;
  padding: 3px 6px;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/DonationCampaigns/DonationCampaigns.vue